<template>
    <ContentComp>
        <FormLayout>
            <FormLayoutRow>
                <FormLayoutLabel text="eventinfobase.location" />
                <InputText
                    :autofocus="true"
                    v-model="model.Name"
                />
            </FormLayoutRow>
            <FormLayoutRow>
                <FormLayoutLabel text="general.country" />
                <SelectCountry v-model="selectedCountryISO2" />
            </FormLayoutRow>
        </FormLayout>
        <FormLayoutRow class="mt-5">
            <FormLayoutLabel text="eventinfobase.coordinates" />
        </FormLayoutRow>
        <MapComp
            class="me-5 w-100 border border-info"
            :tracks="[]"
            :centerpoint="centerPoint"
            :mapmarkers="[marker]"
            :click-to-add-marker="true"
            :zoom="zoomLevel"
            :showCustomControls="false"
            :showSearchBox="true"
            :showmaplabels="true"
            :hide-default-controls="true"
            :height="400"
            @map-clicked="onMapClicked"
        />
    </ContentComp>
</template>
<script setup lang="ts">
import { ContentComp, InputText, MapComp, SelectCountry } from '@/components';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { MapMarker } from '@/components/Map/MapMarker';
import { EventLocation, MapPoint } from '@/objects/generated';
import { useCommonStoreRef } from '@/stores';
import { onMounted, ref, watch } from 'vue';

const model = defineModel<EventLocation>({ required: true });

const { Countries } = useCommonStoreRef();

// The point on the map we clicked
const mappoint = ref<MapPoint>(MapPoint.FromJsValues({}));

// The marker that indicates the position on the map
const marker = ref<MapMarker>(new MapMarker());
marker.value.Type = 'pin';

const props = defineProps<{
    /**
     * The initial value of the location.
     */
    countryIso2?: string;

    /**
     * The initial zoom level of the map.
     */
    zoom?: number;
}>();

const zoomLevel = ref<number>(props.zoom ?? 10);

// We define the default mappoint. This is hardcoded to Oslo (59.9139, 10.7522) but should be depending on the Event
const defaultMappoint = MapPoint.FromJsValues({});
defaultMappoint.Latitude = 59.9139;
defaultMappoint.Longitude = 10.7522;

const centerPoint = ref<MapPoint>(defaultMappoint);

// The selected country ID for the location
const selectedCountryISO2 = ref<string>(props.countryIso2 ?? 'NO'); // Default to Norway (NO)

/**
 * Helper function to se if there is a position.
 * If the Longitude and Latitude is 0 we assume that there is no position
 */
const hasPosition = () => model.value != null && (model.value.Latitude ?? 0) != 0 && (model.value.Longitude ?? 0) != 0;

// If we have a position we set the mappoint to the position of the station
if (hasPosition()) {
    mappoint.value.Latitude = model.value.Latitude!;
    mappoint.value.Longitude = model.value.Longitude!;
    centerPoint.value = mappoint.value;
}

const onMapClicked = (point: MapPoint) => {
    mappoint.value = point;
    marker.value.Point = mappoint.value;
    model.value.Latitude = point.Latitude;
    model.value.Longitude = point.Longitude;
};

watch(
    selectedCountryISO2,
    (newValue) => {
        model.value.CountryISO3 = Countries.value.find((c) => c.ISO2 === newValue)?.ISO3 || null;
    },
    {
        immediate: true,
    },
);

onMounted(() => {
    if (model.value.CountryISO3) {
        selectedCountryISO2.value = Countries.value.find((c) => c.ISO3 === model.value.CountryISO3)?.ISO2 || 'NO';
    }

    // Update the marker position if we have a location
    marker.value.Point = MapPoint.FromJsValues({});
    if (hasPosition()) {
        marker.value.Point.Latitude = model.value.Latitude!;
        marker.value.Point.Longitude = model.value.Longitude!;
    } else {
        marker.value.Point = mappoint.value; // Use the default mappoint
    }
});
</script>
